import React, { useEffect, useState } from "react";
import { api } from "../api";
import MatchCoverage from "../components/football-intelligence/MatchCoverage";
import ResearchOverview from "../components/football-intelligence/ResearchOverview";
import FootballAnalytics from "../components/football-intelligence/FootballAnalytics";
import "../components/football-intelligence/football-intelligence.css";
import "../components/football-intelligence/fi-extra.css";

/**
 * Admin-only football research lab: the model laboratory (collapsed by
 * default) plus a team/player explorer over the cached PitchAPI actuals.
 */
export default function FootballResearchPage() {
  const [kind, setKind] = useState("team");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [identity, setIdentity] = useState("");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [compareIdentity, setCompareIdentity] = useState("");

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(
      () =>
        api
          .get(`/api/football/intelligence/directory/${kind}`, { params: { q, limit: 100 } })
          .then((r) => {
            if (!alive) return;
            setRows(r.data.rows);
            setTotal(r.data.total);
            setError("");
            setIdentity((id) => (r.data.rows.some((x) => x.id === id) ? id : r.data.rows[0]?.id || ""));
          })
          .catch((e) => {
            if (alive) {
              setError(e.response?.data?.detail || "Unable to load research analytics");
              setRows([]);
            }
          }),
      250
    );
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [kind, q]);

  const noun = kind === "player" ? "players" : "teams";

  return (
    <div className="fi-research">
      <div className="fi-eyebrow">PRIVATE LABORATORY / FOOTBALL</div>
      <h1>The detail behind the game.</h1>
      <p>Observed team and player analytics. Research-only provider data; no published picks or production probabilities are changed.</p>

      <MatchCoverage />

      <details>
        <summary style={{ cursor: "pointer", color: "#a5ddba", padding: "14px 0" }}>Open model comparison, data coverage & hypothetical ledger</summary>
        <ResearchOverview />
      </details>

      <div className="fx-toolbar fi-research-tools" style={{ marginTop: 10 }}>
        <div className="fx-chips" role="group" aria-label="Explore">
          {[["team", "Teams"], ["player", "Players"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className="fx-chip"
              aria-pressed={kind === value}
              onClick={() => { setKind(value); setIdentity(""); setCompareIdentity(""); }}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="fx-field">Search
          <input type="search" aria-label="Search analytics directory" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cached names" />
        </label>
        <label className="fx-field">Select {kind}
          <select aria-label="Entity" value={identity} onChange={(e) => setIdentity(e.target.value)}>
            {rows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label className="fx-field">Compare with
          <select aria-label="Compare with" value={compareIdentity} onChange={(e) => setCompareIdentity(e.target.value)}>
            <option value="">No comparison</option>
            {rows.filter((r) => r.id !== identity).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <span className="fx-coverage"><b>{total}</b> {noun} cached{total > rows.length ? ` · listing the first ${rows.length}; refine the search` : ""}</span>
      </div>

      {error && <p role="alert">{error}</p>}
      {identity && <FootballAnalytics kind={kind} identity={identity} namespace="pitchapi" compareIdentity={compareIdentity} forcePrivate />}
    </div>
  );
}
